import { useTranslation, Trans } from "next-i18next";
import parse from "html-react-parser";
import Accordion from "../../components/v2/accordion/accordion";
import ProductResources from "../../components/v2/product/product-resource";
import ProductHeroSection from "../../components/v2/common/product-hero";
import ProductTour from "../../components/v2/product/product-tour";
import '../../styles/scss/_faq-section.module.scss';
import ProductOverviewSection from "../../components/v2/product-overview-section";
import { OpsAI, APM, InfrastructureIcon } from "../../components/v2/icons-v2";
import FeaturesMonitoringV2 from "../../components/v2/tabs/features-monitoring-v2";
import ClientTabsTestimonials from "../../components/v2/mw-carousel/testimonial-with-tabs";
import CallToActionDocsLink from "../../components/v2/blog/call-to-action-docs-link";
import AgentIntegration from "../../components/v2/agent-integration";
import WistiaHeroPlayer from "../../components/home/WistiaHeroPlayer";

export default function LLMObservability() {
    const { t } = useTranslation("common");
    const featuresData = t('product.llm-observability.features.detailedFeatures', { returnObjects: true }) as any[]
    const features = Array.isArray(featuresData) ? featuresData : []

    const faqsData = t('product.llm-observability.faq.questions', { returnObjects: true }) as any[]
    const faqs = Array.isArray(faqsData) ? faqsData : []

    const resourceData = t('product.llm-observability.resources', { returnObjects: true }) as any[]
    const resource = Array.isArray(resourceData) ? resourceData : []

    const productTourData = t('product.llm-observability.productTour', { returnObjects: true }) as any

    const productOverviewData = {
        title: "Product overview",
        buttons: [
            {
                label: "View Docs",
                link: "https://docs.middleware.io/llm-observability/overview",
                className: "ahref-btn primary",
            },
            {
                label: "View Pricing",
                link: "/pricing/",
                className: "ahref-btn secondary",
            },
        ],
        description:
            "Middleware LLM Observability gives AI engineers, platform teams, and SREs complete visibility into every LLM application and AI agent. Monitor prompts, traces, token usage, model performance, stack traces, logs, and GPU metrics from a unified platform. Detect failures faster with correlated traces and logs for faster root cause analysis, evaluate model quality before deployment, and optimize AI performance and costs in production.",
        worksWellWithTitle: "Works Well With",
        worksWellWith: [
            {
                title: "APM",
                link: "/product/apm/",
                icon: <APM />
            },
            {
                title: "OpsAI",
                link: "/product/ops-ai/",
                icon: <OpsAI />
            },
            {
                title: "Infrastructure",
                link: "/product/infrastructure-monitoring/",
                icon: <InfrastructureIcon />
            },


        ],
    };

    const agentDeployData = t('product.shared.LLMObservabilityAgentDeploy', { returnObjects: true }) as any[]
    const agentDeployArray = Array.isArray(agentDeployData) ? agentDeployData : []

    const agentDeploy = agentDeployArray.map(agent => ({
        ...agent,
        icon: <></> // LLM Observability uses empty icons
    }))

    return (
        <>
            <ProductHeroSection title={<h1>{t('product.llm-observability.hero.title')} <span>{t('product.llm-observability.hero.titleHighlight')}</span></h1>}
                description={t('product.llm-observability.hero.description')}
                img={"/images/product/llm-observability.webp"} imgMobile={"/images/product/llm-observability-mobile.webp"} imgAlt={t('product.llm-observability.hero.imageAlt')} preloadImg hasActions={true} className={"llm-page"} tips={t('product.hero.tips')} />


            <ProductOverviewSection productOverviewData={productOverviewData} />

            <FeaturesMonitoringV2 productFeatures={features} tourId={"llm-tour"} />

            <CallToActionDocsLink
                title={'Start monitoring your LLM applications and AI agents in minutes.'}
                docsLink="https://docs.middleware.io/llm-observability/overview"
            />

            <section className="testimonial-section">
                <div className="container">
                    <div className="section-tag testimonial">{t("testimonials.tag")} 👤</div>
                    <h2>Hear it from the best: Why <span className="text-gradient">top companies</span> trust Middleware</h2>

                    <ClientTabsTestimonials />
                </div>
            </section>

            <div className={"product-tour"}>
                <div className={"container"}>
                    <h2>Take an interactive tour of <span className="text-gradient">LLM Observability</span></h2>
                    <p>Explore traces, prompts, evaluations, dashboards, GPU monitoring, and AI agent observability in an interactive walkthrough.</p>

                    <div className={"tour"}>
                        <div className="iframe-container">
                            <WistiaHeroPlayer videoId="u58stn12j3" />
                        </div>
                    </div>

                </div>
            </div>



            <AgentIntegration
                agentDeploy={agentDeploy}
                title={
                    <h2>
                        <Trans
                            i18nKey="product.llm-observability.agentIntegration.title"
                            components={{ highlight: <span className="text-gradient" /> }}
                        />
                    </h2>
                }
                description={"Instrument your LLM applications and AI agents with OpenTelemetry-native observability. Start collecting traces, prompts, logs, token usage, evaluations, and GPU metrics in minutes."}
                contactUsVariant="llm-monitoring"
            />

            <ProductResources resources={resource} title={t('product.shared.resources.title')} />

            <section className={"faq-section"}>
                <div className={"container"}>
                    <h2>{t('product.llm-observability.faq.title')}</h2>
                    <p>{t('product.llm-observability.section.description')}</p>
                    <div className={"faq-item"}>
                        <Accordion faqs={faqs.map(faq => ({
                            ...faq,
                            answerPlain: faq.text,
                            text:
                                faq.textHtml != null && faq.textHtml !== ""
                                    ? parse(faq.textHtml)
                                    : <p>{faq.text}</p>
                        }))} showGroups={true} />
                    </div>
                </div>
            </section>
        </>
    )
}